import React, { useState } from 'react';
import { X, Calendar, Clock, ArrowRight } from 'lucide-react';
import { demoSlots } from '../../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookDemoModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getSelectedTime = () => {
    const slot = demoSlots.find(s => s.id === selectedSlot);
    return slot ? slot.time : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const bookingData = {
        ...formData,
        date: selectedDate,
        time: getSelectedTime()
      };
      
      await axios.post(`${BACKEND_URL}/api/bookings`, bookingData);
      toast.success('Demo booked successfully! We will send you a confirmation email shortly.');
      onClose();
      // Reset form
      setStep(1);
      setSelectedDate('');
      setSelectedSlot(null);
      setFormData({ name: '', email: '', company: '', service: '', message: '' });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book demo. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-white">Book a Demo</h2>
            <p className="text-white/60 mt-1">Schedule a 1-on-1 session with our experts</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 pt-6">
          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#06B6D4]' : 'text-white/40'}`}>
              <div className={`w-8 h-8 flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-[#06B6D4] text-black' : 'bg-white/10 text-white/40'}`}>
                1
              </div>
              <span className="text-sm font-medium">Select Date</span>
            </div>
            <div className="flex-1 h-px bg-white/10"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#06B6D4]' : 'text-white/40'}`}>
              <div className={`w-8 h-8 flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-[#06B6D4] text-black' : 'bg-white/10 text-white/40'}`}>
                2
              </div>
              <span className="text-sm font-medium">Your Details</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              {/* Date Selection */}
              <div>
                <label className="block text-white text-sm font-medium mb-3">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Select a Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                />
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <label className="block text-white text-sm font-medium mb-3">
                    <Clock className="w-4 h-4 inline mr-2" />
                    Select a Time Slot
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {demoSlots.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => slot.available && setSelectedSlot(slot.id)}
                        disabled={!slot.available}
                        className={`px-4 py-3 border transition-all duration-300 ${
                          selectedSlot === slot.id
                            ? 'bg-[#06B6D4] text-black border-[#06B6D4]'
                            : slot.available
                            ? 'bg-white/5 border-white/10 text-white hover:border-[#06B6D4]/50'
                            : 'bg-white/5 border-white/5 text-white/30 cursor-not-allowed'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Button */}
              <button
                onClick={() => setStep(2)}
                disabled={!selectedDate || !selectedSlot}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Company Name</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Service of Interest *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors"
                >
                  <option value="" className="bg-[#121212]">Select a service</option>
                  <option value="ai-agent" className="bg-[#121212]">AI Agent Development</option>
                  <option value="llm" className="bg-[#121212]">LLM Deployment</option>
                  <option value="fullstack" className="bg-[#121212]">Full Stack Development</option>
                  <option value="mobile" className="bg-[#121212]">Mobile App Development</option>
                  <option value="ecommerce" className="bg-[#121212]">Ecommerce Development (Shopify/WooCommerce)</option>
                  <option value="erp" className="bg-[#121212]">ERP Implementation</option>
                  <option value="wms" className="bg-[#121212]">WMS Implementation</option>
                  <option value="aws" className="bg-[#121212]">AWS Cloud Services</option>
                  <option value="supply-chain" className="bg-[#121212]">Supply Chain Digitization</option>
                  <option value="other" className="bg-[#121212]">Other Services</option>
                </select>
              </div>

              <div>
                <label className="block text-white text-sm font-medium mb-2">Additional Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#06B6D4] transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-secondary flex-1"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookDemoModal;
